import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowLeft, Copy, Check, ExternalLink, Share2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TestimonialCollector = () => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({
    name: '',
    role: '',
    company: '',
    rating: 5,
    text: ''
  });

  const collectLink = user 
    ? `${window.location.origin}/submit-testimonial/${user.id}`
    : `${window.location.origin}/submit-testimonial/demo`;

  useEffect(() => {
    if (user) {
      fetchTestimonials();
    }
  }, [user]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/testimonials/my-testimonials');
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(collectLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const addManualTestimonial = async () => {
    if (!newTestimonial.name || !newTestimonial.text) {
      toast.error('Please fill name and testimonial text');
      return;
    }

    try {
      await api.post('/testimonials', newTestimonial);
      toast.success('Testimonial added!');
      setNewTestimonial({ name: '', role: '', company: '', rating: 5, text: '' });
      setShowAddForm(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to add testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const generateEmbedCode = () => {
    const code = `<div id="testimonials-widget" data-user="${user?.id || 'demo'}"></div>
<script src="${window.location.origin}/widgets/testimonials.js"></script>`;
    navigator.clipboard.writeText(code);
    toast.success('Embed code copied!');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="testimonial-collector-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/20 via-background to-orange-500/10 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <Star className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Testimonial Collector</h1>
              <p className="text-muted-foreground">Collect and display customer reviews</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Collection Link */}
        <div className="glass-card p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            Share This Link to Collect Testimonials
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={collectLink}
              readOnly
              className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm"
            />
            <button
              onClick={copyLink}
              className="px-4 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Share this link with your customers to collect their testimonials
          </p>
        </div>

        {/* Add Manual Testimonial */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-500" />
              Add Testimonial Manually
            </h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              {showAddForm ? 'Cancel' : 'Add New'}
            </button>
          </div>

          {showAddForm && (
            <div className="space-y-4 animate-in slide-in-from-top-2">
              <div className="grid md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Customer Name *"
                  value={newTestimonial.name}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <input
                  type="text"
                  placeholder="Role/Position"
                  value={newTestimonial.role}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, role: e.target.value })}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={newTestimonial.company}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, company: e.target.value })}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setNewTestimonial({ ...newTestimonial, rating: star })}
                      className="p-1"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= newTestimonial.rating 
                            ? 'fill-yellow-500 text-yellow-500' 
                            : 'text-gray-500'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                placeholder="Testimonial text *"
                value={newTestimonial.text}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, text: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
              />

              <button
                onClick={addManualTestimonial}
                className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
              >
                Add Testimonial
              </button>
            </div>
          )}
        </div>

        {/* Testimonials List */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Your Testimonials ({testimonials.length})</h3>
            {testimonials.length > 0 && (
              <button
                onClick={generateEmbedCode}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Get Embed Code
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <Star className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No testimonials yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Share the collection link or add testimonials manually
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {testimonials.map((t) => (
                <div key={t.id} className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold">
                        {t.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold">{t.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t.role && t.company ? `${t.role} at ${t.company}` : t.role || t.company || ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= t.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-500'
                            }`}
                          />
                        ))}
                      </div>
                      <button
                        onClick={() => deleteTestimonial(t.id)}
                        className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-muted-foreground italic">"{t.text}"</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialCollector;
